import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input, TextArea } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { FileUpload } from '../components/ui/FileUpload';
import { useAuth } from '../hooks/useAuth';
import { useAssessment } from '../hooks/useAssessment';
import { useAssessmentLibrary } from '../hooks/useAssessmentLibrary';
import { useGlobalEvidence } from '../hooks/useGlobalEvidence';
import { EVIDENCE_TYPES } from '../utils/euAiAct';
import { ShieldCheck, Plus, FileText, Calendar, Trash2, ArrowRight, Filter, Globe, X, AlertCircle } from 'lucide-react';
import { Assessment } from '../types/assessment';
export function AssessmentLibrary() {
  const navigate = useNavigate();
  const {
    user,
    signOut
  } = useAuth();
  const {
    loadAssessment,
    resetAssessment
  } = useAssessment();
  const {
    assessments,
    deleteAssessment
  } = useAssessmentLibrary(user?.email || null);
  const {
    globalEvidence,
    addGlobalEvidence,
    deleteGlobalEvidence,
    isOverdueForReview
  } = useGlobalEvidence(user?.email || null);
  const [filterRegulation, setFilterRegulation] = useState<'all' | 'EU AI Act' | 'NIST AI RMF'>('all');
  const [activeTab, setActiveTab] = useState<'assessments' | 'global-evidence'>('assessments');
  const [showAddGlobalEvidence, setShowAddGlobalEvidence] = useState(false);
  // Global evidence form state
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState(EVIDENCE_TYPES[0]);
  const [owner, setOwner] = useState('');
  const [description, setDescription] = useState('');
  const [nextReviewDate, setNextReviewDate] = useState('');
  const handleSignOut = () => {
    signOut();
    navigate('/');
  };
  const handleNewAssessment = () => {
    resetAssessment();
    navigate('/assessment/system');
  };
  const handleOpenAssessment = (assessment: Assessment) => {
    loadAssessment(assessment);
    if (!assessment.systemDefinition.name) {
      navigate('/assessment/system');
    } else if (!assessment.regulation) {
      navigate('/assessment/regulation');
    } else if (assessment.regulation === 'EU AI Act') {
      if (!assessment.riskLevel) {
        navigate('/assessment/risk');
      } else if (assessment.evidence.length === 0) {
        navigate('/assessment/evidence');
      } else {
        navigate('/assessment/analysis');
      }
    } else if (assessment.regulation === 'NIST AI RMF') {
      navigate('/assessment/nist/govern');
    }
  };
  const handleDeleteAssessment = (assessmentId: string) => {
    if (window.confirm('Delete this assessment? This action cannot be undone.')) {
      deleteAssessment(assessmentId);
    }
  };
  const handleDeleteGlobalEvidence = (evidenceId: string) => {
    if (window.confirm('Delete this global evidence? It will be removed from your library but remain in existing assessments.')) {
      deleteGlobalEvidence(evidenceId);
    }
  };
  const handleFileSelect = (selectedFile: File) => {
    setFile(selectedFile);
    // Auto-populate name with file name (without extension) when file is selected
    if (!name) {
      const nameWithoutExt = selectedFile.name.replace(/\.[^/.]+$/, '');
      setName(nameWithoutExt);
    }
  };
  const handleSubmitGlobalEvidence = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !name) return;
    addGlobalEvidence({
      id: crypto.randomUUID(),
      fileName: file.name,
      name,
      type,
      owner,
      dateUploaded: new Date().toISOString(),
      relevance: description,
      linkedObligations: [],
      description,
      nextReviewDate: nextReviewDate || undefined
    });
    // Reset form
    setFile(null);
    setName('');
    setOwner('');
    setDescription('');
    setNextReviewDate('');
    setShowAddGlobalEvidence(false);
  };
  const calculateProgress = (assessment: Assessment): number => {
    if (!assessment.regulation) return 0;
    let completed = 0;
    let total = 5;
    if (assessment.systemDefinition.name) completed++;
    if (assessment.regulation) completed++;
    if (assessment.riskLevel) completed++;
    if (assessment.evidence.length > 0) completed++;
    if (assessment.evidence.length >= 3) completed++;
    return Math.round(completed / total * 100);
  };
  const filteredAssessments = assessments.filter(a => {
    if (filterRegulation === 'all') return true;
    return a.regulation === filterRegulation;
  });
  const sortedAssessments = [...filteredAssessments].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  // Count overdue evidence
  const overdueCount = globalEvidence.filter(ev => isOverdueForReview(ev)).length;
  return <div className="min-h-screen bg-navy-900" data-id="element-683">
      {/* Header */}
      <header className="bg-navy-800 border-b border-slate-800 sticky top-0 z-50" data-id="element-684">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-685">
          <button onClick={() => navigate('/')} className="flex items-center space-x-3 hover:opacity-80 transition-opacity" data-id="element-686">
            <div className="bg-teal-500/10 p-2 rounded-lg" data-id="element-687">
              <ShieldCheck className="h-6 w-6 text-teal-500" data-id="element-688" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight" data-id="element-689">
              ProvenanceOne
            </span>
          </button>

          <div className="flex items-center space-x-4" data-id="element-690">
            <span className="text-sm text-slate-400 hidden sm:block" data-id="element-691">
              {user?.email}
            </span>
            <Button variant="ghost" onClick={handleSignOut} data-id="element-692">
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-693">
        {/* Tabs */}
        <div className="flex items-center space-x-1 mb-8 border-b border-slate-800" data-id="element-694">
          <button onClick={() => setActiveTab('assessments')} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === 'assessments' ? 'text-teal-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-695">
            Assessments
            {activeTab === 'assessments' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" data-id="element-696" />}
          </button>
          <button onClick={() => setActiveTab('global-evidence')} className={`px-4 py-3 text-sm font-medium transition-colors relative flex items-center space-x-2 ${activeTab === 'global-evidence' ? 'text-teal-400' : 'text-slate-400 hover:text-slate-300'}`} data-id="element-697">
            <Globe className="h-4 w-4" data-id="element-698" />
            <span data-id="element-699">Global Evidence</span>
            {globalEvidence.length > 0 && <Badge variant="teal" className="text-xs" data-id="element-700">
                {globalEvidence.length}
              </Badge>}
            {overdueCount > 0 && <Badge variant="amber" className="text-xs" data-id="element-701">
                {overdueCount} Overdue
              </Badge>}
            {activeTab === 'global-evidence' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" data-id="element-702" />}
          </button>
        </div>

        {/* Assessments Tab */}
        {activeTab === 'assessments' && <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4" data-id="element-703">
              <div data-id="element-704">
                <h1 className="text-3xl font-bold text-white mb-2" data-id="element-705">
                  Assessment Library
                </h1>
                <p className="text-slate-400" data-id="element-706">
                  {sortedAssessments.length}{' '}
                  {sortedAssessments.length === 1 ? 'assessment' : 'assessments'}
                </p>
              </div>

              <Button onClick={handleNewAssessment} size="lg" data-id="element-707">
                <Plus className="h-4 w-4 mr-2" data-id="element-708" />
                New Assessment
              </Button>
            </div>

            {/* Filter */}
            <div className="flex items-center space-x-2 mb-6" data-id="element-709">
              <Filter className="h-4 w-4 text-slate-500" data-id="element-710" />
              <button onClick={() => setFilterRegulation('all')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterRegulation === 'all' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-711">
                All
              </button>
              <button onClick={() => setFilterRegulation('EU AI Act')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterRegulation === 'EU AI Act' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-712">
                EU AI Act
              </button>
              <button onClick={() => setFilterRegulation('NIST AI RMF')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${filterRegulation === 'NIST AI RMF' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-navy-800 text-slate-400 border border-slate-700 hover:border-slate-600'}`} data-id="element-713">
                NIST AI RMF
              </button>
            </div>

            {/* Assessment Grid */}
            {sortedAssessments.length === 0 ? <Card className="text-center py-16 border-dashed" data-id="element-714">
                <FileText className="h-16 w-16 text-slate-600 mx-auto mb-4" data-id="element-715" />
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-716">
                  No assessments yet
                </h3>
                <p className="text-slate-400 mb-6 max-w-md mx-auto" data-id="element-717">
                  Start your first compliance assessment to build a defensible
                  evidence trail.
                </p>
                <Button onClick={handleNewAssessment} data-id="element-718">
                  <Plus className="h-4 w-4 mr-2" data-id="element-719" />
                  Create First Assessment
                </Button>
              </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-720">
                {sortedAssessments.map(assessment => {
            const progress = calculateProgress(assessment);
            return <Card key={assessment.id} className="group hover:border-teal-500/50 transition-all cursor-pointer" onClick={() => handleOpenAssessment(assessment)} data-id="element-721">
                      <div className="space-y-4" data-id="element-722">
                        <div className="flex items-start justify-between" data-id="element-723">
                          <div className="flex-1" data-id="element-724">
                            <div className="flex items-center space-x-2 mb-2" data-id="element-725">
                              <Badge variant={assessment.regulation === 'EU AI Act' ? 'teal' : 'slate'} data-id="element-726">
                                {assessment.regulation || 'Not Selected'}
                              </Badge>
                              <Badge variant={assessment.status === 'completed' ? 'teal' : 'amber'} data-id="element-727">
                                {assessment.status === 'completed' ? 'Completed' : 'In Progress'}
                              </Badge>
                            </div>
                            <h3 className="text-lg font-semibold text-white group-hover:text-teal-400 transition-colors" data-id="element-728">
                              {assessment.systemDefinition.name || 'Untitled Assessment'}
                            </h3>
                            <p className="text-sm text-slate-400 mt-1 line-clamp-2" data-id="element-729">
                              {assessment.systemDefinition.description || 'No description provided'}
                            </p>
                          </div>
                        </div>

                        <div data-id="element-730">
                          <div className="flex items-center justify-between text-sm mb-2" data-id="element-731">
                            <span className="text-slate-400" data-id="element-732">Progress</span>
                            <span className="text-teal-400 font-medium" data-id="element-733">
                              {progress}%
                            </span>
                          </div>
                          <div className="w-full bg-navy-900 h-2 rounded-full overflow-hidden" data-id="element-734">
                            <div className="bg-teal-500 h-full transition-all duration-500" style={{
                      width: `${progress}%`
                    }} data-id="element-735" />
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-800" data-id="element-736">
                          <div className="flex items-center" data-id="element-737">
                            <Calendar className="h-3 w-3 mr-1" data-id="element-738" />
                            {new Date(assessment.updatedAt).toLocaleDateString()}
                          </div>
                          <div className="flex items-center" data-id="element-739">
                            <FileText className="h-3 w-3 mr-1" data-id="element-740" />
                            {assessment.evidence.length} Evidence
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2" data-id="element-741">
                          <button onClick={e => {
                    e.stopPropagation();
                    handleDeleteAssessment(assessment.id);
                  }} className="text-slate-500 hover:text-red-400 transition-colors p-2 -ml-2" aria-label="Delete assessment" data-id="element-742">
                            <Trash2 className="h-4 w-4" data-id="element-743" />
                          </button>
                          <Button size="sm" variant="ghost" className="text-teal-400 hover:text-teal-300" onClick={e => {
                    e.stopPropagation();
                    handleOpenAssessment(assessment);
                  }} data-id="element-744">
                            Continue <ArrowRight className="h-3 w-3 ml-1" data-id="element-745" />
                          </Button>
                        </div>
                      </div>
                    </Card>;
          })}
              </div>}
          </>}

        {/* Global Evidence Tab */}
        {activeTab === 'global-evidence' && <>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4" data-id="element-746">
              <div data-id="element-747">
                <h1 className="text-3xl font-bold text-white mb-2 flex items-center space-x-3" data-id="element-748">
                  <Globe className="h-8 w-8 text-teal-500" data-id="element-749" />
                  <span data-id="element-750">Global Evidence Library</span>
                </h1>
                <p className="text-slate-400" data-id="element-751">
                  Reusable documentation for all your compliance assessments
                </p>
              </div>

              <Button onClick={() => setShowAddGlobalEvidence(true)} size="lg" data-id="element-752">
                <Plus className="h-4 w-4 mr-2" data-id="element-753" />
                Add Global Evidence
              </Button>
            </div>

            {/* Overdue Alert */}
            {overdueCount > 0 && <Card className="mb-8 border-amber-500/30 bg-amber-500/5" data-id="element-754">
                <div className="flex items-start space-x-3" data-id="element-755">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-756" />
                  <div data-id="element-757">
                    <h3 className="font-semibold text-amber-400 mb-1" data-id="element-758">
                      {overdueCount} {overdueCount === 1 ? 'item' : 'items'}{' '}
                      overdue for review
                    </h3>
                    <p className="text-sm text-slate-400" data-id="element-759">
                      Evidence should be reviewed periodically to ensure
                      currency and accuracy.
                    </p>
                  </div>
                </div>
              </Card>}

            {/* Add Global Evidence Form */}
            {showAddGlobalEvidence && <Card className="mb-8 border-teal-500/30 bg-teal-500/5" data-id="element-760">
                <div className="flex items-center justify-between mb-4" data-id="element-761">
                  <h3 className="font-semibold text-white" data-id="element-762">
                    New Global Evidence
                  </h3>
                  <button onClick={() => setShowAddGlobalEvidence(false)} className="text-slate-400 hover:text-white" data-id="element-763">
                    <X className="h-5 w-5" data-id="element-764" />
                  </button>
                </div>

                <form onSubmit={handleSubmitGlobalEvidence} className="space-y-4" data-id="element-765">
                  <FileUpload onFileSelect={handleFileSelect} label={file ? file.name : 'Upload File'} data-id="element-766" />

                  <Input label="Evidence Name" placeholder="e.g. Company AI Ethics Policy" value={name} onChange={e => setName(e.target.value)} required data-id="element-767" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-768">
                    <Select label="Evidence Type" options={EVIDENCE_TYPES.map(t => ({
                value: t,
                label: t
              }))} value={type} onChange={e => setType(e.target.value)} data-id="element-769" />

                    <Input label="Owner" placeholder="e.g. Legal Team" value={owner} onChange={e => setOwner(e.target.value)} required data-id="element-770" />
                  </div>

                  <Input label="Next Review Date" type="date" value={nextReviewDate} onChange={e => setNextReviewDate(e.target.value)} data-id="element-771" />
                  <p className="text-xs text-slate-500 -mt-2" data-id="element-772">
                    When should this evidence be reviewed for currency?
                  </p>

                  <TextArea label="Description" placeholder="Describe this evidence for future reference..." rows={3} value={description} onChange={e => setDescription(e.target.value)} required data-id="element-773" />

                  <div className="flex space-x-3" data-id="element-774">
                    <Button type="submit" disabled={!file || !name || !owner || !description} data-id="element-775">
                      Save to Global Library
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setShowAddGlobalEvidence(false)} data-id="element-776">
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>}

            {/* Global Evidence List */}
            {globalEvidence.length === 0 ? <Card className="text-center py-16 border-dashed" data-id="element-777">
                <Globe className="h-16 w-16 text-slate-600 mx-auto mb-4" data-id="element-778" />
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-779">
                  No global evidence yet
                </h3>
                <p className="text-slate-400 mb-6 max-w-md mx-auto" data-id="element-780">
                  Add reusable evidence like company policies, certifications,
                  or standard procedures that apply across multiple assessments.
                </p>
                <Button onClick={() => setShowAddGlobalEvidence(true)} data-id="element-781">
                  <Plus className="h-4 w-4 mr-2" data-id="element-782" />
                  Add First Evidence
                </Button>
              </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-783">
                {globalEvidence.map(evidence => {
            const isOverdue = isOverdueForReview(evidence);
            return <Card key={evidence.id} className={`group transition-all ${isOverdue ? 'border-amber-500/50 bg-amber-500/5' : 'hover:border-teal-500/50'}`} data-id="element-784">
                      <div className="space-y-4" data-id="element-785">
                        <div className="flex items-start justify-between" data-id="element-786">
                          <div className="flex items-start space-x-3 flex-1" data-id="element-787">
                            <div className={`h-10 w-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${isOverdue ? 'bg-amber-500/10 border-amber-500/30' : 'bg-teal-500/10 border-teal-500/30'}`} data-id="element-788">
                              <FileText className={`h-5 w-5 ${isOverdue ? 'text-amber-500' : 'text-teal-500'}`} data-id="element-789" />
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-790">
                              <div className="flex items-center space-x-2 mb-1" data-id="element-791">
                                <h3 className="text-base font-semibold text-white truncate" data-id="element-792">
                                  {evidence.name}
                                </h3>
                                {isOverdue && <Badge variant="amber" className="text-xs flex-shrink-0" data-id="element-793">
                                    Overdue
                                  </Badge>}
                              </div>
                              <div className="flex items-center space-x-2 mt-1" data-id="element-794">
                                <Badge variant="teal" className="text-xs" data-id="element-795">
                                  {evidence.type}
                                </Badge>
                              </div>
                              <p className="text-xs text-slate-500 mt-1 truncate" data-id="element-796">
                                {evidence.fileName}
                              </p>
                            </div>
                          </div>
                        </div>

                        {evidence.description && <p className="text-sm text-slate-400 line-clamp-2" data-id="element-797">
                            {evidence.description}
                          </p>}

                        <div className="flex flex-col space-y-2 text-xs text-slate-500 pt-2 border-t border-slate-800" data-id="element-798">
                          <div className="flex items-center justify-between" data-id="element-799">
                            <div className="flex items-center" data-id="element-800">
                              <Calendar className="h-3 w-3 mr-1" data-id="element-801" />
                              Uploaded:{' '}
                              {new Date(evidence.dateUploaded).toLocaleDateString()}
                            </div>
                            <span data-id="element-802">{evidence.owner}</span>
                          </div>
                          {evidence.nextReviewDate && <div className={`flex items-center ${isOverdue ? 'text-amber-400 font-medium' : ''}`} data-id="element-803">
                              <Calendar className="h-3 w-3 mr-1" data-id="element-804" />
                              Review:{' '}
                              {new Date(evidence.nextReviewDate).toLocaleDateString()}
                            </div>}
                        </div>

                        <div className="flex items-center justify-between pt-2" data-id="element-805">
                          <button onClick={() => handleDeleteGlobalEvidence(evidence.id)} className="text-slate-500 hover:text-red-400 transition-colors p-2 -ml-2" aria-label="Delete global evidence" data-id="element-806">
                            <Trash2 className="h-4 w-4" data-id="element-807" />
                          </button>
                          <Badge variant="slate" className="text-xs" data-id="element-808">
                            <Globe className="h-3 w-3 mr-1" data-id="element-809" />
                            Global
                          </Badge>
                        </div>
                      </div>
                    </Card>;
          })}
              </div>}
          </>}
      </main>
    </div>;
}